import { FormControlLabel, Switch } from '@mui/material';
import { Button, Modal, Text } from '@shared/components';
import { Attachment } from '@shared/components/attachment/Attachment';
import { DocRenderer } from '@shared/components/doc-viewer/DocViewer';
import { MentionInput } from '@shared/components/mention-input/MentionInput';
import { useToast } from '@shared/components/toast';
import { ChatViewContextGetter } from '@shared/contexts/ChatWindowContext';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useChatUploadFiles as useUploadFiles } from '@shared/features/chats-page/hooks/useChatUploadFiles';
import {
  UploadableEntity,
  useAddChatMessageToConversationMutation,
} from '@shared/generated/graphql';
import {
  appendChatMessageToCache,
  markConversationWorkingInCache,
} from '@shared/graphql/cache/conversationCache';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { usePlatform } from '@shared/hooks/usePlatform';
import type { ChatViewContext } from 'clerk_common';
import { Paperclip } from 'lucide-react';
import {
  type ClipboardEvent as ReactClipboardEvent,
  type DragEvent as ReactDragEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { v4 as uuidv4 } from 'uuid';
import { OptimisticChatMessage } from './optimisticChatMessage';

type ChatMessageInputProps = {
  conversationId: string | null;
  createConversation?: (viewContext?: ChatViewContext) => Promise<string>;
  getViewContext?: ChatViewContextGetter;
  hasMirrorableExternalChannel?: boolean;
  organizationId: string;
  initialDraft?: MessageDraft;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  onConversationStarted?: (conversationId: string, draft: MessageDraft) => void;
  onInitialDraftConsumed?: () => void;
  onOptimisticMessageRemove?: (messageId: string) => void;
  onOptimisticMessageUpdate?: (message: OptimisticChatMessage) => void;
  tabCompletion?: string;
};

const CHAT_PLACEHOLDER = 'Ask a question or give Vee a task…';
const ACCEPTED_FILE_TYPES =
  '.csv,.docx,.eml,.html,.jpeg,.jpg,.json,.md,.pdf,.png,.tif,.txt,.xls,.xlsm,.xlsx';
const NOOP_CONVERSATION_STARTED = () => undefined;
const NOOP_INITIAL_DRAFT_CONSUMED = () => undefined;
const NOOP_REMOVE_OPTIMISTIC_MESSAGE = () => undefined;
const NOOP_UPDATE_OPTIMISTIC_MESSAGE = () => undefined;
const REJECT_CREATE_CONVERSATION = () =>
  Promise.reject(new Error('Cannot start a conversation from this input'));

export type MessageDraft = {
  attachmentDocumentIds: string[];
  attachmentNames: string[];
  message: string;
  mirrorToExternalChannels: boolean;
  viewContext?: ChatViewContext;
};

type LocalFilePreview = {
  fileName: string;
  objectUrl: string;
};

function getChatDraftStorageKey(
  organizationId: string,
  conversationId: string | null
): string {
  return `chat-draft:${organizationId}:${conversationId ?? 'new'}`;
}

export function saveNewChatDraft(organizationId: string, message: string) {
  const key = getChatDraftStorageKey(organizationId, null);
  window.sessionStorage.setItem(key, JSON.stringify(message));
  // NOTE(wassim): an already-open composer only re-reads its draft on this
  // usehooks-ts event, so a bare setItem would not reach it.
  window.dispatchEvent(new StorageEvent('session-storage', { key }));
}

export function ChatMessageInput({
  conversationId,
  createConversation = REJECT_CREATE_CONVERSATION,
  getViewContext,
  hasMirrorableExternalChannel = true,
  organizationId,
  initialDraft,
  inputRef: externalInputRef,
  onConversationStarted = NOOP_CONVERSATION_STARTED,
  onInitialDraftConsumed = NOOP_INITIAL_DRAFT_CONSUMED,
  onOptimisticMessageRemove = NOOP_REMOVE_OPTIMISTIC_MESSAGE,
  onOptimisticMessageUpdate = NOOP_UPDATE_OPTIMISTIC_MESSAGE,
  tabCompletion,
}: ChatMessageInputProps) {
  const { isVoomaAdmin } = useMeContext();
  const { track } = useAnalytics();
  const { isWeb } = usePlatform();
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [newMessage, setNewMessage, clearSavedDraft] = useSessionStorage(
    getChatDraftStorageKey(organizationId, conversationId),
    '',
    { initializeWithValue: false }
  );
  const [isStartingConversation, setIsStartingConversation] = useState(false);
  const [localFilePreview, setLocalFilePreview] =
    useState<LocalFilePreview | null>(null);
  const [shouldMirrorToExternalChannels, setShouldMirrorToExternalChannels] =
    useState(true);
  const [addChatMessage, { loading: isAddingMessage }] =
    useAddChatMessageToConversationMutation({
      update: (cache, { data }, { variables }) => {
        const message = data?.addChatMessageToConversation.externalMessage;
        const targetConversationId = variables?.input.conversationId;
        if (message && targetConversationId) {
          appendChatMessageToCache(cache, targetConversationId, message);
          markConversationWorkingInCache(cache, targetConversationId);
        }
      },
    });
  const { sendToast } = useToast();
  const ownInputRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = externalInputRef ?? ownInputRef;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { remove, resetUploader, startUploads, uploadStates } = useUploadFiles({
    organizationId,
    subjectType: UploadableEntity.EXTERNAL_MESSAGE_DOCUMENT_UPLOAD,
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, [conversationId, inputRef]);

  const hasSentInitialDraftRef = useRef(false);
  useEffect(() => {
    if (!initialDraft || !conversationId || hasSentInitialDraftRef.current) {
      return;
    }
    hasSentInitialDraftRef.current = true;
    onInitialDraftConsumed();
    void sendDraft(conversationId, initialDraft, uuidv4());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const localFileObjectUrl = localFilePreview?.objectUrl;
    return function revokeLocalFileObjectUrl() {
      if (localFileObjectUrl) {
        URL.revokeObjectURL(localFileObjectUrl);
      }
    };
  }, [localFilePreview]);

  const attachmentDocumentIds = uploadStates.flatMap(({ uploadId }) =>
    uploadId ? [uploadId] : []
  );
  const hasIncompleteUploads = uploadStates.some(
    ({ hasError, isUploading, uploadId }) =>
      hasError || isUploading || !uploadId
  );
  const isSending = isAddingMessage || isStartingConversation;
  const isSubmitDisabled =
    isSending ||
    hasIncompleteUploads ||
    (!newMessage.trim() && uploadStates.length === 0);

  async function persistMessage(
    targetConversationId: string,
    draft: MessageDraft
  ): Promise<string | undefined> {
    const context = draft.viewContext ?? (await getViewContext?.());
    const result = await addChatMessage({
      variables: {
        input: {
          attachmentDocumentIds: draft.attachmentDocumentIds,
          conversationId: targetConversationId,
          ...(context ? { context } : {}),
          message: draft.message,
          mirrorToExternalChannels: draft.mirrorToExternalChannels,
        },
      },
    });
    return result.data?.addChatMessageToConversation.externalMessage?.id;
  }

  function reportSendFailure(error: unknown) {
    sendToast('Failed to send message', {
      variant: 'error',
      description: error instanceof Error ? error.message : 'Unknown error',
    });
    console.error(error);
  }

  async function sendDraft(
    targetConversationId: string,
    draft: MessageDraft,
    optimisticMessageId: string,
    createdAt = new Date()
  ) {
    const retry = () =>
      void sendDraft(
        targetConversationId,
        draft,
        optimisticMessageId,
        createdAt
      );
    const optimisticMessage: OptimisticChatMessage = {
      attachmentNames: draft.attachmentNames,
      createdAt,
      id: optimisticMessageId,
      message: draft.message,
      retry,
      status: 'sending',
    };
    onOptimisticMessageUpdate(optimisticMessage);

    try {
      const persistedMessageId = await persistMessage(
        targetConversationId,
        draft
      );
      if (!persistedMessageId) {
        onOptimisticMessageRemove(optimisticMessageId);
        return;
      }
      onOptimisticMessageUpdate({
        ...optimisticMessage,
        persistedMessageId,
        status: 'persisted',
      });
      track('Sent Chat Message', {
        conversationId: targetConversationId,
        data: { attachmentCount: draft.attachmentDocumentIds.length },
      });
    } catch (error) {
      onOptimisticMessageUpdate({
        ...optimisticMessage,
        status: 'failed',
      });
      reportSendFailure(error);
    }
  }

  // NOTE(wassim): Only the (fast) create round-trip blocks here; the message
  // itself is sent optimistically by the thread that opens for the new
  // conversation, so the first send feels like any other.
  // NOTE(mike): The first message reuses this view context so the page isn't
  // captured (or permission-prompted) a second time.
  async function startConversationWithDraft(draft: MessageDraft) {
    setIsStartingConversation(true);
    try {
      const viewContext = await getViewContext?.();
      const newConversationId = await createConversation(viewContext);
      if (!newConversationId) {
        throw new Error('Could not start a new conversation');
      }
      clearDraft();
      onConversationStarted(newConversationId, { ...draft, viewContext });
    } catch (error) {
      reportSendFailure(error);
    } finally {
      setIsStartingConversation(false);
    }
  }

  function buildDraft(): MessageDraft {
    return {
      attachmentDocumentIds,
      attachmentNames: uploadStates.map(
        ({ file }) => file?.name ?? 'Attachment'
      ),
      message: newMessage.trim(),
      mirrorToExternalChannels:
        hasMirrorableExternalChannel &&
        (!isVoomaAdmin || shouldMirrorToExternalChannels),
    };
  }

  function clearDraft() {
    clearSavedDraft();
    resetUploader();
  }

  function handleSubmit() {
    if (isSubmitDisabled) return;

    const draft = buildDraft();
    if (!conversationId) {
      void startConversationWithDraft(draft);
      return;
    }
    clearDraft();
    void sendDraft(conversationId, draft, uuidv4());
  }

  function handleFileDrag(event: ReactDragEvent<HTMLDivElement>) {
    if (!event.dataTransfer.types.includes('Files')) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsDraggingFiles(true);
  }

  function handleFileDragLeave(event: ReactDragEvent<HTMLDivElement>) {
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    ) {
      return;
    }
    setIsDraggingFiles(false);
  }

  function handleFileDrop(event: ReactDragEvent<HTMLDivElement>) {
    if (!event.dataTransfer.types.includes('Files')) return;

    event.preventDefault();
    setIsDraggingFiles(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      startUploads(files);
    }
  }

  function handleFilePaste(event: ReactClipboardEvent<HTMLDivElement>) {
    const itemFiles = Array.from(event.clipboardData.items)
      .filter(({ kind }) => kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
    const files =
      itemFiles.length > 0 ? itemFiles : Array.from(event.clipboardData.files);
    if (files.length === 0) return;

    if (event.clipboardData.getData('text/plain').length === 0) {
      event.preventDefault();
    }
    startUploads(files);
  }

  function openLocalFilePreview(file: File) {
    setLocalFilePreview({
      fileName: file.name,
      objectUrl: URL.createObjectURL(file),
    });
  }

  function handleLocalFilePreviewOpenChange(isOpen: boolean) {
    if (!isOpen) setLocalFilePreview(null);
  }

  return (
    <div
      aria-label="Message input"
      className={
        isDraggingFiles
          ? 'ring-brand-300 rounded-lg ring-2 ring-offset-2'
          : 'rounded-lg'
      }
      role="group"
      onDragEnter={handleFileDrag}
      onDragLeave={handleFileDragLeave}
      onDragOver={handleFileDrag}
      onDrop={handleFileDrop}
      onPaste={handleFilePaste}
    >
      <MentionInput
        inputRef={inputRef}
        placeholder={CHAT_PLACEHOLDER}
        tabCompletion={tabCompletion}
        textSize="md"
        value={newMessage}
        onChange={setNewMessage}
        submit={{
          canSubmitWithoutText: uploadStates.length > 0,
          onSubmit: handleSubmit,
          loading: isSending,
          isDisabled: isSubmitDisabled,
        }}
        footer={
          <div className="flex w-full flex-col gap-2">
            {uploadStates.length > 0 && (
              <div className="flex max-h-40 w-[calc(100%+2.5rem)] flex-col gap-2 overflow-y-auto">
                {uploadStates.map((upload) => {
                  const file = upload.file;
                  return (
                    <Attachment
                      key={upload.id}
                      className="w-full"
                      errorMsg={
                        upload.hasError ? 'Error uploading file' : undefined
                      }
                      filetype={file?.type}
                      hasRemoveButton
                      isUploading={upload.isUploading}
                      name={file?.name ?? 'Attachment'}
                      onPress={
                        isWeb && upload.uploadId && file
                          ? () => openLocalFilePreview(file)
                          : undefined
                      }
                      onRemove={() => remove(upload.id)}
                      onRetryUpload={() => {
                        remove(upload.id);
                        if (file) {
                          startUploads([file]);
                        }
                      }}
                    />
                  );
                })}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                accept={ACCEPTED_FILE_TYPES}
                className="hidden"
                multiple
                type="file"
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  if (files.length > 0) {
                    startUploads(files);
                  }
                  event.target.value = '';
                }}
              />
              <Button
                aria-label="Attach files"
                className="!pl-0"
                icon={<Paperclip size={12} />}
                iconPosition="left"
                isGhost
                size="xs"
                onPress={() => fileInputRef.current?.click()}
              >
                Attach
              </Button>
              {isVoomaAdmin && hasMirrorableExternalChannel && (
                <FormControlLabel
                  className="!m-0"
                  control={
                    <Switch
                      checked={shouldMirrorToExternalChannels}
                      color="primary"
                      onChange={(_, checked) =>
                        setShouldMirrorToExternalChannels(checked)
                      }
                      size="small"
                    />
                  }
                  label={
                    <Text type="body-xs" className="text-gray-400">
                      Mirror to external channel (Slack, Teams, or email)
                    </Text>
                  }
                />
              )}
            </div>
          </div>
        }
      />
      {isWeb && (
        <Modal.Root
          open={localFilePreview !== null}
          onOpenChange={handleLocalFilePreviewOpenChange}
        >
          {localFilePreview && (
            <Modal.Content size="large" aria-describedby={undefined}>
              <Modal.Header title={localFilePreview.fileName} hasCloseButton />
              <div className="h-[80vh] min-h-0">
                <DocRenderer
                  url={localFilePreview.objectUrl}
                  fileName={localFilePreview.fileName}
                />
              </div>
            </Modal.Content>
          )}
        </Modal.Root>
      )}
    </div>
  );
}
